// AuthModal — F8.7b · premium-dark overlay modal with 3 tabs.
//
// Managed by AuthModalContext. Closes on: ESC, backdrop click, X button,
// or programmatically via closeModal(). Google button up top, then form.
import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useAuthModal } from "@/context/AuthModalContext";
import GoogleLoginButton from "./GoogleLoginButton";
import LoginForm from "./LoginForm";
import SignupForm from "./SignupForm";
import ForgotPasswordForm from "./ForgotPasswordForm";

export default function AuthModal() {
    const { open, tab, prefillEmail, closeModal, setTab } = useAuthModal();
    const { t } = useTranslation("landing_v2");
    const [canClose, setCanClose] = React.useState(false);

    // ESC to close
    useEffect(() => {
        if (!open) { setCanClose(false); return undefined; }
        const onKey = (e) => { if (e.key === "Escape") closeModal(); };
        document.addEventListener("keydown", onKey);
        // Lock body scroll while open
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        // Allow backdrop-click close only after 200ms — protects against the
        // triggering click event bubbling up to the newly-mounted backdrop.
        const timer = setTimeout(() => setCanClose(true), 200);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
            clearTimeout(timer);
        };
    }, [open, closeModal]);

    const handleBackdropClick = () => { if (canClose) closeModal(); };

    const currentTitle = tab === "signup"
        ? t("auth.modal.tab_signup")
        : tab === "forgot"
            ? t("auth.modal.tab_forgot")
            : t("auth.modal.tab_login");

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="auth-modal-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    onClick={handleBackdropClick}
                    className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                    data-testid="auth-modal-backdrop"
                >
                    <motion.div
                        key="auth-modal-card"
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-[420px] bg-[#15161B] border border-white/10 ring-1 ring-brand-primary/10 rounded-2xl shadow-[0_20px_80px_rgba(0,0,0,0.6)] overflow-hidden"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="auth-modal-title"
                        data-testid="auth-modal-card"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-white/5">
                            <h2
                                id="auth-modal-title"
                                className="text-text-main text-base font-semibold tracking-tight"
                                data-testid="auth-modal-title"
                            >
                                {currentTitle}
                            </h2>
                            <button
                                type="button"
                                onClick={closeModal}
                                aria-label={t("auth.modal.close_aria")}
                                className="p-1.5 -mr-1.5 rounded-lg text-text-secondary hover:text-text-main hover:bg-white/5 transition-colors"
                                data-testid="auth-modal-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Tabs (visible for login/signup; forgot has its own layout) */}
                        {tab !== "forgot" && (
                            <div className="flex px-6 pt-4">
                                <div className="flex w-full bg-bg/60 border border-brand-border rounded-lg p-0.5">
                                    <button
                                        type="button"
                                        onClick={() => setTab("login")}
                                        className={`flex-1 text-sm py-1.5 rounded-md transition-colors ${
                                            tab === "login"
                                                ? "bg-[#1F1F1F] text-white font-medium"
                                                : "text-[#CCCCCC] hover:text-white hover:bg-white/5"
                                        }`}
                                        data-testid="auth-modal-tab-login"
                                    >
                                        {t("auth.modal.tab_login")}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setTab("signup")}
                                        className={`flex-1 text-sm py-1.5 rounded-md transition-colors ${
                                            tab === "signup"
                                                ? "bg-[#1F1F1F] text-white font-medium"
                                                : "text-[#CCCCCC] hover:text-white hover:bg-white/5"
                                        }`}
                                        data-testid="auth-modal-tab-signup"
                                    >
                                        {t("auth.modal.tab_signup")}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Body */}
                        <div className="px-6 py-5 space-y-4">
                            {tab !== "forgot" && (
                                <>
                                    <GoogleLoginButton label={t("auth.modal.google_button")} />
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 h-px bg-brand-border" />
                                        <span className="text-[11px] uppercase tracking-wide text-text-secondary">
                                            {t("auth.modal.or_divider")}
                                        </span>
                                        <div className="flex-1 h-px bg-brand-border" />
                                    </div>
                                </>
                            )}
                            {tab === "login" && <LoginForm prefillEmail={prefillEmail} />}
                            {tab === "signup" && <SignupForm prefillEmail={prefillEmail} />}
                            {tab === "forgot" && <ForgotPasswordForm prefillEmail={prefillEmail} />}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
